import { useRef } from "react"
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { Paperclip, X } from "lucide-react"
import { toast } from "sonner"

const DEFAULT_MAX_FILES = 20
const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "image/png",
  "image/jpeg",
  "image/gif",
]

interface TemplateFileSelectorProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  className?: string
  existingCount?: number
  iconOnly?: boolean
  maxFiles?: number
  maxFileSize?: number
  // Render only the staged-file chips; the file input trigger lives elsewhere
  // (e.g. inside an AttachmentAddMenu).
  hideTrigger?: boolean
}

export default function TemplateFileSelector({
  files,
  onFilesChange,
  className,
  existingCount = 0,
  iconOnly = false,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  hideTrigger = false,
}: TemplateFileSelectorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || [])
    if (newFiles.length === 0) return

    const totalFiles = existingCount + files.length + newFiles.length
    if (totalFiles > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed.`)
      return
    }

    for (const file of newFiles) {
      if (file.size > maxFileSize) {
        toast.error(`${file.name} is too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB.`)
        return
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name} has an unsupported file type.`)
        return
      }
    }

    onFilesChange([...files, ...newFiles])

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleRemoveFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index))
  }

  const truncateFileName = (name: string, maxLength: number = 20) => {
    if (name.length <= maxLength) return name
    const ext = name.split(".").pop() || ""
    const nameWithoutExt = name.slice(0, name.length - ext.length - 1)
    const truncatedName = nameWithoutExt.slice(0, maxLength - ext.length - 4) + "..."
    return `${truncatedName}.${ext}`
  }

  const canAddMore = existingCount + files.length < maxFiles

  if (iconOnly) {
    return (
      <div className={cn("flex flex-wrap items-center gap-2", className)}>
        {files.map((file, index) => (
          <div key={`${file.name}-${index}`} className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs">
            <Paperclip className="size-3" />
            <span className="max-w-[100px] truncate">{truncateFileName(file.name)}</span>
            <button
              type="button"
              onClick={() => handleRemoveFile(index)}
              className="text-icon hover:text-destructive ml-0.5 rounded-sm"
            >
              <X className="size-3" />
            </button>
          </div>
        ))}
        {canAddMore && !hideTrigger && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_FILE_TYPES.join(",")}
              onChange={handleFileSelect}
              className="hidden"
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="iconXs"
                    variant={files.length > 0 ? "secondary" : "ghost"}
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                  >
                    <Paperclip className="text-icon h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start">
                  Attach files
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {files.map((file, index) => (
        <div key={`${file.name}-${index}`} className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs">
          <Paperclip className="size-3" />
          <span className="max-w-[100px] truncate">{truncateFileName(file.name)}</span>
          <button
            type="button"
            onClick={() => handleRemoveFile(index)}
            className="text-icon hover:text-destructive ml-0.5 rounded-sm"
          >
            <X className="size-3" />
          </button>
        </div>
      ))}

      {canAddMore && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_FILE_TYPES.join(",")}
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="outline"
            size="xs"
            onClick={() => fileInputRef.current?.click()}
            type="button"
            className="text-muted-foreground"
          >
            <Paperclip className="size-3" />
            {files.length === 0 ? "Attach" : "Add more"}
          </Button>
        </>
      )}
    </div>
  )
}
